<template>
  <v-card variant="flat">
    <v-card-text>
      <v-row>
        <v-col v-if="!isMobile" cols="2">
          <v-sheet rounded="lg">
            <v-list-item
              v-for="(item, index) in listItems"
              :key="index"
              :class="{ active: currentTab === item.name, 'disabled-item': !item.isEnabled }"
              :disabled="!item.isEnabled"
              @click="selectTab(item)">
              {{ item.label }}
              <v-badge
                v-if="item.name == 'notes' && teamNoteCount > 0"
                color="error"
                :content="teamNoteCount"
                inline></v-badge>
              <v-icon
                v-if="item.name === 'submit-film' && registrationStore.registrationCompleted"
                color="success"
                icon="mdi-check-circle"></v-icon>
              <v-icon
                v-if="item.name === 'submit-film' && !registrationStore.registrationCompleted"
                color="error"
                icon="mdi-alert-circle"></v-icon>
            </v-list-item>
          </v-sheet>
        </v-col>
        <v-col>
          <v-sheet min-height="70vh" rounded="lg">
            <component
              v-bind="componentProps"
              :is="currentComponent"
              @snack="throwSnack"></component>
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>

  <!-- Mobile bottom navigation -->
  <v-bottom-navigation v-if="isMobile" v-model="safeTab" class="sticky-bottom pa-3">
    <v-btn
      v-for="(item, index) in listItems"
      :key="index"
      :class="[
        { active: currentTab === item.name, 'disabled-item': !item.isEnabled },
        'text-h6',
        'ma-1',
      ]"
      :value="item.name"
      :disabled="!item.isEnabled">
      <v-icon v-if="item.icon" :icon="item.icon" :color="colorInfo(item)"></v-icon>
      <span class="text-subtitle-1">
        {{ item.label }}
      </span>
      <v-badge
        v-if="item.name == 'notes' && teamNoteCount > 0"
        color="error"
        :content="teamNoteCount"
        inline />
    </v-btn>
  </v-bottom-navigation>
</template>
<script lang="ts">
export interface ISideNavigation {
  name: string;
  label: string;
  component: DefineComponent; // Properly typed as Vue component
  isEnabled: boolean;
  props?: object;
  icon?: string;
}
</script>

<script setup lang="ts">
import { ref, computed, watch, markRaw, DefineComponent } from 'vue';
import { useRegistrationStore, useTeamStore, useYearStore } from '@/stores/';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { TeamCriteriaDTO, TeamDTO } from '@/dtos';
import { useSnack } from '@/helpers';
import { SNACKSTATUS } from '@/stores/util';
import TeamInfoSubView from '@/views/user/teams/subviews/TeamInfoSubView.vue';
import SignUpView from '@/views/user/signup/SignUpView.vue';
import TurnInHomeView from './turnin/TurnInHomeView.vue';
import CriteriaSelectionView from './criteria/CriteriaSelectionView.vue';
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);

const teamStore = useTeamStore();
const yearStore = useYearStore();
const registrationStore = useRegistrationStore();
const route = useRoute();
const router = useRouter();
const team = computed(() => teamStore.myTeam ?? new TeamDTO());
const teamNoteCount = ref(0);
const { data: regData } = registrationStore.getMyRegistration;
const myReg = computed(() => regData ?? new TeamCriteriaDTO());
const isRegistered = computed(() => myReg.value.isRegistered());
const canSelectCritiera = computed(() => myReg.value.canSelectCriteria());
const canSubmitFilm = computed(() => myReg.value.canSubmitFilm());
const { smAndDown } = useDisplay();
let isMobile = computed(() => smAndDown.value);

const year = computed(() =>
  yearStore.selectedYear.value ? yearStore.selectedYear.value : yearStore.currentYear,
);
const yearId = computed(() => year.value?.id ?? '');
const teamId = computed(() => team.value?.id ?? '');
const branchYear = computed(() => myReg.value.branchYear ?? undefined);
const props = computed(() => ({
  yearId: yearId.value,
  teamId: teamId.value,
  branchYear: branchYear.value,
}));

const safeTab = computed({
  get: () => currentTab.value,
  set: (val: string) => {
    if (val) {
      currentTab.value = val;
    }
  },
});
const listItems = computed<ISideNavigation[]>(() => {
  const items: ISideNavigation[] = [
    {
      name: 'info',
      label: 'Home',
      component: markRaw(TeamInfoSubView) as unknown as DefineComponent,
      props: { reg: myReg.value },
      isEnabled: true,
      icon: 'mdi-home',
    },
  ];

  if (
    year.value &&
    !isRegistered.value &&
    year.value.start_date &&
    new Date(year.value.start_date) <= new Date()
  ) {
    items.push({
      name: 'signup',
      label: 'Register Now',
      component: markRaw(SignUpView) as DefineComponent,
      isEnabled: true,
    });
  }

  if (canSelectCritiera.value === true) {
    items.push({
      name: 'select-criteria',
      get label() {
        return registrationStore.hasSelectedCriteria ? 'Selected Criteria' : 'Select Criteria';
      },
      component: markRaw(CriteriaSelectionView) as unknown as DefineComponent,
      isEnabled: true,
      props: props.value,
      icon: 'mdi-checkbox-marked-outline',
    });
  }

  if (canSubmitFilm.value === true) {
    items.push({
      name: 'submit-film',
      label: 'Submit Film',
      component: markRaw(TurnInHomeView as unknown as DefineComponent),
      isEnabled: true,
      props: props.value,
      icon: 'mdi-movie-edit-outline',
    });
  }

  return items;
});

const currentTab = ref<string>(
  Array.isArray(route.params.tabName) ? route.params.tabName[0] : (route.params.tabName ?? 'info'),
);

let currentComponent = computed(() => {
  const item = listItems.value.find((i) => i.name === currentTab.value);
  return item ? item.component : null;
});

const selectTab = (tabName: ISideNavigation) => {
  console.log('tabName.name', tabName.name);
  currentTab.value = tabName.name;
};

watch(
  currentTab,
  () => {
    if (currentTab.value == '' && currentTab.value == '') {
      currentTab.value = 'info';
    }
    router.push({ name: 'Home', params: { tabName: currentTab.value } });
  },
  { immediate: true },
);
watch(
  () => route.params.tabName,
  (newTab) => {
    if (newTab && newTab !== currentTab.value) {
      currentTab.value = Array.isArray(newTab) ? newTab[0] : newTab;
    }
  },
);

let componentProps = computed(() => {
  const item = listItems.value.find((i) => i.name === currentTab.value);
  return item ? item.props : null;
});

const colorInfo = (item: ISideNavigation) => {
  if (item.name === 'submit-film') {
    if (registrationStore.registrationCompleted) {
      return 'success';
    } else {
      return 'error';
    }
  }
  if (item.name == 'select-criteria') {
    if (registrationStore.hasSelectedCriteria) {
      return 'success';
    } else {
      return 'error';
    }
  }
  return undefined;
};
</script>

<style scoped>
.disabled-item {
  color: gray !important;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
}
.sticky-bottom {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}
</style>
